<template>
  <main class="booking-query">
    <div class="bq-inner">
      <router-link :to="{ name: 'BookingHome' }" class="bq-back">← 美容預約首頁</router-link>

      <h1 class="bq-title">查詢預約</h1>
      <p class="bq-subtitle">使用 LINE 或 Email 查詢您的預約紀錄</p>

      <!-- LINE 查詢區塊 -->
      <div class="bq-form bq-form--line">
        <!-- 已登入 LINE：顯示使用者資訊 -->
        <div v-if="lineUser" class="bq-line-user">
          <img
            v-if="lineUser.picture"
            :src="lineUser.picture"
            :alt="lineUser.name"
            class="bq-line-user__avatar"
          />
          <span class="bq-line-user__name">{{ lineUser.name }}</span>
          <button class="bq-line-user__logout" @click="switchLineAccount">切換帳號</button>
        </div>

        <!-- 未登入 LINE：顯示登入按鈕 -->
        <button v-else class="bq-btn--line" @click="loginWithLine">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="22" height="22" rx="5" fill="white" fill-opacity="0.2"/>
            <path d="M11 3C6.582 3 3 6.134 3 10.008c0 3.49 3.095 6.41 7.28 6.924.284.06.67.187.768.43.088.222.058.57.028.793l-.124.742c-.038.222-.175.87.763.474.938-.396 5.058-2.978 6.9-5.098C19.833 12.76 20 11.42 20 10.008 20 6.134 16.418 3 11 3z" fill="white"/>
          </svg>
          用 LINE 查詢預約
        </button>
        <!-- 狀態說明 -->
        <div class="bq-status-legend">
          <p class="bq-status-legend__title">預約狀態說明</p>
          <p>🕐 <strong>待確認</strong> — 請主動傳送預約訊息，通知店家確認</p>
          <p>✅ <strong>已確認</strong> — 店家已確認，預約成立</p>
        </div>

        <!-- LINE 查詢中 -->
        <div v-if="lineLoading" class="bq-line-loading">
          <span class="bq-spinner bq-spinner--sm" /> 查詢中…
        </div>

        <!-- LINE API 錯誤 -->
        <p v-if="lineError" class="bq-api-error" style="margin-top: 12px;">{{ lineError }}</p>
      </div>

      <!-- 分隔線 -->
      <div class="bq-divider">或</div>

      <!-- Email 查詢表單 -->
      <form class="bq-form" @submit.prevent="onSearch">
        <div class="bq-field">
          <label for="q-email">Email <span class="bq-required">*</span></label>
          <input
            id="q-email"
            v-model.trim="form.email"
            type="email"
            placeholder="example@mail.com"
            class="bq-input"
            :class="{ 'bq-input--error': errors.email }"
            autocomplete="email"
          />
          <p v-if="errors.email" class="bq-field-error">{{ errors.email }}</p>
        </div>

        <div class="bq-field">
          <label for="q-phone">手機末 4 碼 <span class="bq-required">*</span></label>
          <input
            id="q-phone"
            v-model.trim="form.phone_last4"
            type="text"
            inputmode="numeric"
            maxlength="4"
            placeholder="例：7788"
            class="bq-input bq-input--narrow"
            :class="{ 'bq-input--error': errors.phone_last4 }"
          />
          <p v-if="errors.phone_last4" class="bq-field-error">{{ errors.phone_last4 }}</p>
        </div>

        <!-- API 錯誤：泛用訊息 -->
        <p v-if="bookingStore.queryError" class="bq-api-error">
          {{ bookingStore.queryError }}
        </p>

        <button
          type="submit"
          class="bq-btn bq-btn--primary"
          :disabled="bookingStore.queryLoading"
        >
          <span v-if="bookingStore.queryLoading" class="bq-spinner bq-spinner--sm bq-spinner--white" />
          {{ bookingStore.queryLoading ? '查詢中…' : '查詢預約' }}
        </button>
      </form>

      <!-- 查詢結果：多筆 -->
      <transition name="fade">
        <div v-if="reservations.length > 0" class="bq-results">
          <p class="bq-results__count">共找到 {{ reservations.length }} 筆預約紀錄</p>

          <div
            v-for="r in reservations"
            :key="r.reservation_code"
            class="bq-result"
          >
            <!-- 狀態標籤 + 代碼 -->
            <div class="bq-result__header">
              <span class="bq-status" :class="`bq-status--${r.status}`">
                {{ statusLabel(r.status) }}
              </span>
              <span class="bq-result__code">{{ r.reservation_code }}</span>
            </div>

            <!-- 待確認：提醒主動通知店家 -->
            <p v-if="r.status === 'pending'" class="bq-result__pending-hint">
              ⚠️ 請主動傳送預約訊息，通知店家確認
            </p>

            <!-- 已確認：確認訊息 -->
            <p v-else-if="r.status === 'confirmed'" class="bq-result__confirmed-hint">
              🎉 店家已確認，您的預約正式成立，期待您的光臨！
            </p>

            <!-- 待確認：複製預約訊息 + 開啟 LINE（店家有 LINE 就顯示，email/LINE 查詢都適用）-->
            <div v-if="r.status === 'pending' && r.store_line_id" class="bq-result__line-action">
              <button class="bq-btn--copy-sm" @click="copyMsg(r)">
                {{ copiedCode === r.reservation_code ? '✅ 已複製訊息' : '📋 複製預約訊息' }}
              </button>
              <button class="bq-btn--line bq-btn--line-sm" @click="openLineApp(r.store_line_id)">
                <svg viewBox="0 0 24 24" fill="currentColor" style="width:18px;height:18px;flex-shrink:0">
                  <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.070 9.437-6.977C23.176 14.393 24 12.458 24 10.314"/>
                </svg>
                開啟 LINE 傳送
              </button>
            </div>

            <!-- 預約詳情 -->
            <dl class="bq-result__list">
              <dt>店家</dt>
              <dd>{{ r.store_name }}</dd>

              <dt>服務</dt>
              <dd>
                {{ r.service_name }}
                <span v-if="r.service_item_name" class="bq-result__sub"> · {{ r.service_item_name }}</span>
                <span v-if="r.duration_minutes" class="bq-result__sub">
                  （約 {{ r.duration_minutes }} 分鐘）
                </span>
              </dd>

              <dt>日期</dt>
              <dd>{{ fmtDate(r.slot_date) }}</dd>

              <dt>時間</dt>
              <dd>
                {{ fmtTime(r.start_time) }}
                <span v-if="r.end_time"> – {{ fmtTime(r.end_time) }}</span>
              </dd>

              <dt>姓名</dt>
              <dd>{{ r.customer_name }}</dd>

              <dt>手機</dt>
              <dd>{{ r.customer_phone }}</dd>
            </dl>

            <!-- 取消 / 變更區塊 -->
            <div
              v-if="['pending_email', 'pending', 'confirmed'].includes(r.status) && !isSlotPast(r)"
              class="bq-result__actions"
            >
              <p v-if="r.status === 'pending_email'" class="bq-result__actions-note bq-result__actions-note--warn">
                ⚠️ 此預約尚未完成 Email 驗證，請前往信箱點擊「點此送出，待店家確認」連結。
              </p>
              <p v-else class="bq-result__actions-note">
                如需取消或變更，請於預約時間 <strong>48 小時前</strong>操作
              </p>

              <!-- 操作結果訊息 -->
              <p v-if="actionMsg[r.reservation_code]" class="bq-action-msg"
                 :class="actionMsgType[r.reservation_code] === 'error' ? 'bq-action-msg--error' : 'bq-action-msg--ok'">
                {{ actionMsg[r.reservation_code] }}
              </p>

              <div class="bq-action-btns">
                <!-- 線上取消 -->
                <button
                  class="bq-btn bq-btn--cancel"
                  :disabled="actionLoading[r.reservation_code]"
                  @click="lineUser && r.line_user_id ? doCancelByLine(r) : doCancel(r)"
                >
                  <span v-if="actionLoading[r.reservation_code]" class="bq-spinner bq-spinner--sm bq-spinner--white" />
                  取消預約
                </button>

                <!-- 變更時間：開啟時段選擇面板（email / LINE 皆可） -->
                <button
                  class="bq-btn bq-btn--reschedule"
                  :disabled="actionLoading[r.reservation_code]"
                  @click="openReschedule(r)"
                >
                  變更時間
                </button>

                <!-- 致電店家 -->
                <a
                  v-if="r.store_phone"
                  :href="`tel:${r.store_phone}`"
                  class="bq-btn bq-btn--call"
                >
                  致電店家
                </a>
              </div>

            </div>

            <p
              v-else-if="['pending_email', 'pending', 'confirmed'].includes(r.status) && isSlotPast(r)"
              class="bq-result__terminal bq-result__terminal--expired"
            >
              此預約時段已過，如需處理請直接聯絡店家。
            </p>
            <p
              v-else-if="r.status === 'cancelled'"
              class="bq-result__terminal bq-result__terminal--cancelled"
            >
              此預約已取消。
            </p>
            <p
              v-else-if="r.status === 'completed'"
              class="bq-result__terminal bq-result__terminal--completed"
            >
              此預約已完成，感謝您的光臨。
            </p>
          </div>

          <!-- 狀態說明 -->
          <div class="bq-status-legend bq-status-legend--result">
            <p class="bq-status-legend__title">預約狀態說明</p>
            <p>🕐 <strong>待確認</strong> — 請主動傳送預約訊息，通知店家確認</p>
            <p>✅ <strong>已確認</strong> — 店家已確認，預約成立</p>
          </div>

          <!-- 再查一筆 -->
          <button class="bq-btn bq-btn--secondary bq-btn--sm" @click="reset">
            重新查詢
          </button>
        </div>
      </transition>
    </div>
  </main>

  <!-- 變更時間 Modal -->
  <Teleport to="body">
    <Transition name="bq-rs-fade">
      <div v-if="rsPanel.code" class="bq-rs-overlay" @click.self="closeReschedule">
        <div class="bq-rs-modal">
          <div class="bq-rs-modal__header">
            <span class="bq-rs-modal__title">📅 選擇新時間</span>
            <button class="bq-rs-close" @click="closeReschedule">✕</button>
          </div>
          <p class="bq-rs-note">
            變更後新預約狀態為「待確認」，請記得通知店家確認
          </p>
          <div class="bq-rs-date-row">
            <label class="bq-rs-label">選擇日期</label>
            <input
              type="date"
              v-model="rsPanel.date"
              :min="minDate"
              class="bq-input bq-rs-date-input"
              @change="fetchRsSlots"
            />
          </div>
          <div v-if="rsPanel.slotsLoading" class="bq-rs-loading">
            <span class="bq-spinner bq-spinner--sm" /> 載入時段中…
          </div>
          <p v-else-if="rsPanel.slotsError" class="bq-rs-error">{{ rsPanel.slotsError }}</p>
          <div v-else-if="rsPanel.slots.length > 0" class="bq-rs-slots">
            <button
              v-for="slot in rsPanel.slots"
              :key="slot.id"
              class="bq-rs-slot"
              :class="{
                'bq-rs-slot--selected': rsPanel.selectedSlotId === slot.id,
                'bq-rs-slot--full': slot.available <= 0
              }"
              :disabled="slot.available <= 0"
              @click="slot.available > 0 && (rsPanel.selectedSlotId = slot.id)"
            >
              {{ fmtTime(slot.start_time) }}
              <span class="bq-rs-slot__end"> – {{ calcSlotEnd(slot.start_time, rsPanel.durationMinutes) }}</span>
              <span v-if="slot.available <= 0" class="bq-rs-slot__full-tag">已額滿</span>
            </button>
          </div>
          <p v-if="rsPanel.error" class="bq-rs-error bq-rs-error--submit">{{ rsPanel.error }}</p>
          <div class="bq-rs-footer">
            <button
              class="bq-btn bq-btn--primary"
              :disabled="!rsPanel.selectedSlotId || rsPanel.submitting"
              @click="submitReschedule"
            >
              <span v-if="rsPanel.submitting" class="bq-spinner bq-spinner--sm bq-spinner--white" />
              {{ rsPanel.submitting ? '變更中…' : '確認變更' }}
            </button>
            <button class="bq-btn bq-btn--secondary" @click="closeReschedule">取消</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- 變更成功提醒 Modal -->
  <Teleport to="body">
    <Transition name="bq-rs-fade">
      <div v-if="rsSuccess" class="bq-rs-overlay" @click.self="closeRsSuccess">
        <div class="bq-rs-modal bq-rs-modal--success">
          <div class="bq-rs-modal__header">
            <span class="bq-rs-modal__title">✅ 變更成功</span>
            <button class="bq-rs-close" @click="closeRsSuccess">✕</button>
          </div>

          <div class="bq-rs-success-body">
            <p class="bq-rs-success-msg">
              新預約已建立，舊預約已自動取消。<br>
              新預約狀態為「待確認」，<strong>請記得主動通知店家確認</strong>。
            </p>

            <!-- 複製 + LINE（店家有 LINE OA 才顯示）-->
            <div v-if="rsSuccess.store_line_id" class="bq-result__line-action bq-rs-success-actions">
              <button class="bq-btn--copy-sm" @click="copyRsMsg">
                {{ rsCopied ? '✅ 已複製訊息' : '📋 複製預約訊息' }}
              </button>
              <button class="bq-btn--line bq-btn--line-sm" @click="openLineApp(rsSuccess.store_line_id)">
                <svg viewBox="0 0 24 24" fill="currentColor" style="width:18px;height:18px;flex-shrink:0">
                  <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.070 9.437-6.977C23.176 14.393 24 12.458 24 10.314"/>
                </svg>
                開啟 LINE 傳送
              </button>
            </div>
          </div>

          <div class="bq-rs-footer">
            <button class="bq-btn bq-btn--primary" style="flex:1" @click="closeRsSuccess">
              我知道了
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { storeLineUrl } from '@/utils/lineLink.js'
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useBookingStore } from '@/store/bookingStore'

const route        = useRoute()
const bookingStore = useBookingStore()

// ── LINE 常數 ─────────────────────────────────────────

// ── LINE 狀態 ─────────────────────────────────────────
const lineUser    = ref(null)   // { name, user_id, picture }
const lineLoading = ref(false)
const lineError   = ref('')

const form = ref({
  email:       '',
  phone_last4: '',
})

const errors = ref({
  email:       '',
  phone_last4: '',
})

const reservations   = ref([])
const actionLoading  = ref({})   // { [reservation_code]: bool }
const actionMsg      = ref({})   // { [reservation_code]: string }
const actionMsgType  = ref({})   // { [reservation_code]: 'ok' | 'error' }
const copiedCode     = ref('')   // 目前已複製的 reservation_code
const rsSuccess      = ref(null) // 變更成功後的新預約資料（顯示成功彈窗）
const rsCopied       = ref(false)

// ── onMounted：讀取 URL 參數 ──────────────────────────
onMounted(async () => {
  // 原有：route.query.email
  if (route.query.email) {
    form.value.email = route.query.email
  }

  // 從浮動按鈕點「LINE 查詢」直接跳 LINE 登入
  if (route.query.via === 'line' && !route.query.line_user_id) {
    loginWithLine()
    return
  }

  // LINE OAuth 回調後帶入的參數
  const { line_name, line_user_id, line_picture } = route.query
  if (line_user_id) {
    lineUser.value = {
      name:    line_name    || '',
      user_id: line_user_id || '',
      picture: line_picture || '',
    }

    // 清除 URL 中的 LINE 參數（不影響 email 參數）
    const cleanQuery = { ...route.query }
    delete cleanQuery.line_name
    delete cleanQuery.line_user_id
    delete cleanQuery.line_picture
    const search = new URLSearchParams(cleanQuery).toString()
    history.replaceState(null, '', window.location.pathname + (search ? '?' + search : ''))

    // 自動查詢
    await queryByLine()
  }
})

// ── LINE 登入 ─────────────────────────────────────────
function loginWithLine() {
  // 透過 server 跳轉，避免 Android Chrome 將 access.line.me 交給 LINE App 處理
  const returnUrl = window.location.pathname + window.location.search
  window.location.href = `/api/auth/line/start?returnUrl=${encodeURIComponent(returnUrl)}`
}

function switchLineAccount() {
  lineUser.value  = null
  lineError.value = ''
  reservations.value = []
  loginWithLine()
}

// ── LINE 查詢 ─────────────────────────────────────────
async function queryByLine() {
  if (!lineUser.value?.user_id) return
  lineLoading.value = true
  lineError.value   = ''
  reservations.value = []

  try {
    const res = await fetch(
      `/api/booking/reservations/by-line?line_user_id=${encodeURIComponent(lineUser.value.user_id)}`
    )
    const data = await res.json()
    if (data.success) {
      reservations.value = data.reservations || []
      if (reservations.value.length === 0) {
        lineError.value = '此 LINE 帳號尚無預約紀錄'
      }
    } else {
      lineError.value = data.message || '查詢失敗，請稍後再試'
    }
  } catch {
    lineError.value = '網路錯誤，請稍後再試'
  } finally {
    lineLoading.value = false
  }
}

// ── LINE 取消 ─────────────────────────────────────────
async function doCancelByLine(r) {
  const timeLabel = `${fmtDate(r.slot_date)} ${fmtTime(r.start_time)}`
  if (!confirm(`確定取消 ${timeLabel} 的預約？\n取消後無法復原。`)) return

  const code = r.reservation_code
  actionLoading.value[code] = true
  actionMsg.value[code]     = ''

  try {
    const res = await fetch(`/api/booking/reservations/${code}/cancel-by-line`, {
      method:  'PUT',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ line_user_id: lineUser.value.user_id }),
    })
    const data = await res.json()

    if (data.success) {
      const idx = reservations.value.findIndex(x => x.reservation_code === code)
      if (idx !== -1) reservations.value[idx] = { ...reservations.value[idx], status: 'cancelled' }
      actionMsg.value[code]     = '✅ 取消成功'
      actionMsgType.value[code] = 'ok'
    } else {
      actionMsg.value[code]     = data.message || '取消失敗，請稍後再試'
      actionMsgType.value[code] = 'error'
    }
  } catch {
    actionMsg.value[code]     = '網路錯誤，請稍後再試'
    actionMsgType.value[code] = 'error'
  } finally {
    actionLoading.value[code] = false
  }
}

// ── 表單驗證 ──────────────────────────────────────────
function validate() {
  let ok = true
  errors.value = { email: '', phone_last4: '' }

  if (!form.value.email) {
    errors.value.email = '請輸入 Email'
    ok = false
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    errors.value.email = 'Email 格式錯誤'
    ok = false
  }
  if (!form.value.phone_last4) {
    errors.value.phone_last4 = '請輸入手機末 4 碼'
    ok = false
  } else if (!/^\d{4}$/.test(form.value.phone_last4)) {
    errors.value.phone_last4 = '手機末 4 碼需為 4 位數字'
    ok = false
  }
  return ok
}

async function onSearch() {
  reservations.value           = []
  bookingStore.queryError      = null
  bookingStore.queriedReservations = []
  lineUser.value               = null
  lineError.value              = ''
  if (!validate()) return

  const result = await bookingStore.queryByEmail(
    form.value.email,
    form.value.phone_last4
  )
  if (result.success) {
    reservations.value = result.reservations || []
  }
}

function reset() {
  reservations.value               = []
  actionLoading.value              = {}
  actionMsg.value                  = {}
  actionMsgType.value              = {}
  form.value.email                 = ''
  form.value.phone_last4           = ''
  errors.value                     = { email: '', phone_last4: '' }
  bookingStore.queryError          = null
  bookingStore.queriedReservations = []
  lineError.value                  = ''
}

// ── 線上取消（email 模式）────────────────────────────
async function doCancel(r) {
  const timeLabel = `${fmtDate(r.slot_date)} ${fmtTime(r.start_time)}`
  if (!confirm(`確定取消 ${timeLabel} 的預約？\n取消後無法復原，確認信將寄至您的信箱。`)) return

  const code = r.reservation_code
  actionLoading.value[code] = true
  actionMsg.value[code]     = ''

  const result = await bookingStore.cancelGuestReservation(
    code, form.value.email, form.value.phone_last4
  )

  actionLoading.value[code] = false

  if (result.success) {
    // 本地更新狀態，不重新查詢
    const idx = reservations.value.findIndex(x => x.reservation_code === code)
    if (idx !== -1) reservations.value[idx] = { ...reservations.value[idx], status: 'cancelled' }
    actionMsg.value[code]     = '✅ 取消成功，確認信已寄至您的信箱'
    actionMsgType.value[code] = 'ok'
  } else {
    actionMsg.value[code]     = result.message || '取消失敗，請稍後再試'
    actionMsgType.value[code] = 'error'
  }
}

// ── 變更時間：內嵌時段選擇面板 ─────────────────────
const rsPanel = ref({
  code:            null,   // 正在變更的 reservation_code
  storeId:         null,   // 店家 ID（供 slots API 使用）
  durationMinutes: 0,      // 服務時長
  teacherName:     null,   // 原本預約的老師（篩選時段用）
  date:            '',
  slots:           [],
  slotsLoading:    false,
  slotsError:      '',
  selectedSlotId:  null,
  submitting:      false,
  error:           '',
})

const minDate = computed(() => new Date().toISOString().slice(0, 10))

function openReschedule(r) {
  rsPanel.value = {
    code:            r.reservation_code,
    storeId:         r.store_id,
    durationMinutes: Number(r.duration_minutes) || 0,
    teacherName:     r.teacher_name || null,
    date:            '',
    slots:           [],
    slotsLoading:    false,
    slotsError:      '',
    selectedSlotId:  null,
    submitting:      false,
    error:           '',
  }
}

function closeReschedule() {
  rsPanel.value.code = null
}

function closeRsSuccess() {
  rsSuccess.value = null
}

function copyRsMsg() {
  if (!rsSuccess.value) return
  const msg = buildRescheduleMsg(rsSuccess.value)
  navigator.clipboard.writeText(msg).then(() => {
    rsCopied.value = true
    setTimeout(() => { rsCopied.value = false }, 4000)
  }).catch(() => {})
}

async function fetchRsSlots() {
  const { storeId, durationMinutes, date, teacherName } = rsPanel.value
  if (!date || !storeId) return
  rsPanel.value.slotsLoading   = true
  rsPanel.value.slotsError     = ''
  rsPanel.value.slots          = []
  rsPanel.value.selectedSlotId = null
  try {
    // 帶入原預約的 teacher_name，確保只看同一個老師的時段
    // 這樣其他老師同時段有空也不會誤顯示為可選
    const paramObj = { date, duration: durationMinutes, exclude_code: rsPanel.value.code }
    if (teacherName) paramObj.teacher_name = teacherName
    const params = new URLSearchParams(paramObj)
    const res    = await fetch(`/api/booking/stores/${storeId}/slots?${params}`)
    const data   = await res.json()
    if (data.success) {
      // 依 start_time 排序後去重（同時段只保留第一筆，有空位的優先）
      // available <= 0 的時段仍保留，讓畫面顯示「已額滿」
      const sorted = (data.slots || []).slice().sort((a, b) =>
        a.start_time.localeCompare(b.start_time)
      )
      const seen   = new Set()
      const unique = sorted.filter(s => {
        if (seen.has(s.start_time)) return false
        seen.add(s.start_time)
        return true
      })
      rsPanel.value.slots = unique
      if (rsPanel.value.slots.length === 0) rsPanel.value.slotsError = '此日期沒有可預約時段'
    } else {
      rsPanel.value.slotsError = data.message || '無法載入時段'
    }
  } catch {
    rsPanel.value.slotsError = '網路錯誤，請稍後再試'
  } finally {
    rsPanel.value.slotsLoading = false
  }
}

async function submitReschedule() {
  const { code, selectedSlotId } = rsPanel.value
  if (!code || !selectedSlotId) return
  rsPanel.value.submitting = true
  rsPanel.value.error      = ''
  // 先記下舊預約時間（成功後組「原本→變更」訊息用）
  const oldRes = reservations.value.find(x => x.reservation_code === code)
  try {
    // 優先用當下登入的 LINE user_id；否則用 email + phone_last4
    const currentLineId = lineUser.value?.user_id || null
    const body = currentLineId
      ? { line_user_id: currentLineId, new_slot_id: selectedSlotId }
      : { email: form.value.email, phone_last4: form.value.phone_last4, new_slot_id: selectedSlotId }

    const res  = await fetch(`/api/booking/reservations/${code}/reschedule`, {
      method:  'PUT',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
    })
    const data = await res.json()
    if (data.success) {
      // 舊預約標記已取消
      const idx = reservations.value.findIndex(x => x.reservation_code === code)
      if (idx !== -1) reservations.value[idx] = { ...reservations.value[idx], status: 'cancelled' }
      // 新預約插到列表最前面
      if (data.reservation) reservations.value.unshift(data.reservation)
      closeReschedule()
      // 開啟變更成功提醒彈窗（讓客人主動通知店家）
      rsCopied.value  = false
      rsSuccess.value = data.reservation
        ? { ...data.reservation, old_slot_date: oldRes?.slot_date, old_start_time: oldRes?.start_time, old_end_time: oldRes?.end_time }
        : null
    } else {
      rsPanel.value.error = data.message || '變更失敗，請稍後再試'
    }
  } catch {
    rsPanel.value.error = '網路錯誤，請稍後再試'
  } finally {
    rsPanel.value.submitting = false
  }
}

// ── LINE 複製訊息並開啟 OA ──────────────────────────
function buildLineMsg(r) {
  const lines = ['您好，我剛完成預約，請協助確認！']
  if (r.service_name) {
    const svc = r.service_item_name ? `${r.service_name} · ${r.service_item_name}` : r.service_name
    lines.push(`課程：${svc}`)
  }
  if (r.slot_date || r.start_time) {
    const timeRange = r.end_time ? `${fmtTime(r.start_time)} ~ ${fmtTime(r.end_time)}` : fmtTime(r.start_time)
    lines.push(`時間：${fmtDate(r.slot_date)} ${timeRange}`.trim())
  }
  lines.push('謝謝！')
  return lines.join('\n')
}

function buildRescheduleMsg(r) {
  const lines = ['您好，我剛完成預約時間變更，請協助確認！']
  if (r.service_name) {
    const svc = r.service_item_name ? `${r.service_name} · ${r.service_item_name}` : r.service_name
    lines.push(`課程：${svc}`)
  }
  if (r.old_slot_date || r.old_start_time) {
    const oldRange = r.old_end_time
      ? `${fmtTime(r.old_start_time)} ~ ${fmtTime(r.old_end_time)}`
      : fmtTime(r.old_start_time)
    lines.push(`原本時間：${fmtDate(r.old_slot_date)} ${oldRange}`.trim())
  }
  if (r.slot_date || r.start_time) {
    const newRange = r.end_time
      ? `${fmtTime(r.start_time)} ~ ${fmtTime(r.end_time)}`
      : fmtTime(r.start_time)
    lines.push(`變更時間：${fmtDate(r.slot_date)} ${newRange}`.trim())
  }
  lines.push('謝謝！')
  return lines.join('\n')
}

function lineOaUrl(storeLineId) {
  return storeLineUrl(storeLineId)
}
function openLineApp(storeLineId) {
  window.location.href = lineOaUrl(storeLineId)
}

function copyMsg(r) {
  const msg = buildLineMsg(r)
  navigator.clipboard.writeText(msg).then(() => {
    copiedCode.value = r.reservation_code
    setTimeout(() => { copiedCode.value = '' }, 4000)
  }).catch(() => {})
}

// 時段是否已過期（本地時間比較）
function isSlotPast(r) {
  const dateStr = String(r.slot_date).slice(0, 10)
  const [h, m]  = (r.start_time || '00:00').split(':').map(Number)
  const slotStart = new Date(
    `${dateStr}T${String(h % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}:00+08:00`
  )
  return slotStart < new Date()
}

// 狀態中文標籤
function statusLabel(s) {
  return {
    pending_email: '待驗證 Email',
    pending:       '待確認',
    confirmed:     '已確認',
    completed:     '已完成',
    cancelled:     '已取消',
  }[s] ?? s
}

// 依開始時間 + 時長計算結束時間
function calcSlotEnd(startTime, durationMins) {
  if (!startTime || !durationMins) return ''
  const [h, m] = startTime.split(':').map(Number)
  const total  = h * 60 + m + Number(durationMins)
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

// 時間格式化
function fmtTime(t) {
  if (!t) return ''
  const [h, m] = t.split(':').map(Number)
  return `${String(h % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// 日期格式化：相容 'YYYY-MM-DD' 字串 或 ISO 字串 'YYYY-MM-DDTHH:...'
function fmtDate(d) {
  if (!d) return ''
  return String(d).slice(0, 10)
}
</script>

<style scoped>
.booking-query { padding: 24px 16px 80px; }
.bq-inner {
  max-width: 560px;
  margin: 0 auto;
}

.bq-back {
  display: inline-block;
  font-size: 0.88rem;
  color: #8b6f5c;
  text-decoration: none;
  margin-bottom: 20px;
}
.bq-back:hover { text-decoration: underline; }

.bq-title {
  font-size: 1.7rem;
  color: #333;
  margin: 0 0 8px;
}
.bq-subtitle {
  font-size: 0.93rem;
  color: #888;
  margin: 0 0 28px;
  line-height: 1.5;
}

/* 表單 */
.bq-form {
  background: #f9f6f3;
  border-radius: 12px;
  padding: 24px 20px;
  margin-bottom: 32px;
}
.bq-form--line {
  margin-bottom: 0;
}
.bq-field {
  margin-bottom: 20px;
}
.bq-field label {
  display: block;
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 6px;
}
.bq-required { color: #c0392b; }
.bq-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s;
  -webkit-appearance: none;
}
.bq-input:focus {
  outline: none;
  border-color: #8b6f5c;
}
.bq-input--error  { border-color: #c0392b; }
.bq-input--narrow { max-width: 120px; }
.bq-field-error {
  font-size: 0.82rem;
  color: #c0392b;
  margin-top: 4px;
}

.bq-api-error {
  font-size: 0.9rem;
  color: #c0392b;
  background: #fdf0ef;
  border: 1px solid #f5c6c3;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 16px;
  line-height: 1.5;
}

/* 按鈕 */
.bq-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s;
  box-sizing: border-box;
}
.bq-btn--primary {
  width: 100%;
  background: #8b6f5c;
  color: #fff;
}
.bq-btn--primary:hover:not(:disabled) { background: #7a5f4d; }
.bq-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
.bq-btn--secondary {
  background: transparent;
  border: 1px solid #8b6f5c;
  color: #8b6f5c;
}
.bq-btn--secondary:hover { background: #f5ede8; }
.bq-btn--call {
  background: #27ae60;
  color: #fff;
  width: 100%;
  font-size: 0.95rem;
  padding: 12px 16px;
}
.bq-btn--call:hover { background: #219a52; }
.bq-btn--sm { font-size: 0.88rem; padding: 8px 18px; margin-top: 20px; }

/* 結果列表 */
.bq-results__count {
  font-size: 0.88rem;
  color: #888;
  margin-bottom: 16px;
}
.bq-result {
  background: #fff;
  border: 1px solid #e8ddd7;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
}
.bq-result__header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.bq-result__code {
  font-size: 0.82rem;
  color: #aaa;
  margin: 0;
  word-break: break-all;
}

/* 狀態標籤 */
.bq-status {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 600;
}
.bq-status--pending_email { background: #fff0b3; color: #5a4000; }
.bq-status--pending   { background: #fef3cd; color: #856404; }
.bq-status--confirmed { background: #d1ecf1; color: #0c5460; }
.bq-status--completed { background: #d4edda; color: #155724; }
.bq-status--cancelled { background: #f8d7da; color: #721c24; }

/* 詳情列表 */
.bq-result__list {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 8px 12px;
  font-size: 0.92rem;
  margin: 0 0 20px;
  padding: 0;
}
.bq-result__list dt { color: #aaa; font-weight: normal; }
.bq-result__list dd { margin: 0; color: #444; word-break: break-word; }
.bq-result__sub { color: #aaa; font-size: 0.85em; }

/* 操作區塊 */
.bq-result__actions {
  background: #f9f6f3;
  border-radius: 8px;
  padding: 16px;
}
.bq-result__actions-note {
  font-size: 0.85rem;
  color: #888;
  margin: 0 0 12px;
  line-height: 1.5;
}
.bq-result__actions-note--warn {
  color: #856404;
  background: #fff3cd;
  border-radius: 6px;
  padding: 8px 12px;
}
.bq-action-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.bq-action-msg {
  font-size: 0.88rem;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
  line-height: 1.5;
}
.bq-action-msg--ok    { background: #d4edda; color: #155724; }
.bq-action-msg--error { background: #f8d7da; color: #721c24; }
.bq-btn--cancel {
  background: transparent;
  border: 1px solid #e74c3c;
  color: #e74c3c;
  padding: 10px 20px;
}
.bq-btn--cancel:hover:not(:disabled) { background: #fdf0ef; }
.bq-btn--cancel:disabled { opacity: 0.6; cursor: not-allowed; }
.bq-btn--reschedule {
  background: transparent;
  border: 1px solid #8b6f5c;
  color: #8b6f5c;
  padding: 10px 20px;
}
.bq-btn--reschedule:hover:not(:disabled) { background: #f5ede8; }
.bq-btn--reschedule:disabled { opacity: 0.6; cursor: not-allowed; }

/* Terminal */
.bq-result__terminal {
  font-size: 0.9rem;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 0;
}
.bq-result__terminal--pending-email { background: #fff3cd; color: #856404; }
.bq-result__terminal--cancelled { background: #f8d7da; color: #721c24; }
.bq-result__terminal--completed { background: #d4edda; color: #155724; }
.bq-result__terminal--expired   { background: #f0f0f0; color: #888; }

/* Spinner */
.bq-spinner {
  display: inline-block;
  width: 28px; height: 28px;
  border: 3px solid #ddd;
  border-top-color: #8b6f5c;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
.bq-spinner--sm    { width: 18px; height: 18px; border-width: 2px; }
.bq-spinner--white { border-color: rgba(255,255,255,0.3); border-top-color: #fff; }
@keyframes spin { to { transform: rotate(360deg); } }

/* 進場動畫 */
.fade-enter-active { transition: opacity 0.3s, transform 0.3s; }
.fade-enter-from   { opacity: 0; transform: translateY(8px); }

/* LINE 查詢相關 */
.bq-divider { display: flex; align-items: center; gap: 12px; margin: 24px 0; color: #bbb; font-size: 0.88rem; }
.bq-divider::before, .bq-divider::after { content: ''; flex: 1; height: 1px; background: #e8ddd7; }
.bq-btn--line { background: #06c755; color: #fff; width: 100%; justify-content: center; gap: 10px; padding: 14px 24px; border-radius: 8px; border: none; font-size: 1rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; transition: background 0.15s; }
.bq-btn--line:hover { background: #05b34c; }
.bq-btn--line-sm { padding: 10px 16px; font-size: 0.88rem; width: 100%; }
.bq-btn--copy-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 16px;
  border-radius: 8px;
  border: 2px solid #06c755;
  background: #fff;
  color: #06c755;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.bq-btn--copy-sm:hover { background: #f0faf4; }
.bq-result__line-action { display: flex; flex-direction: column; gap: 8px; margin: 8px 0 4px; }

.bq-status-legend {
  margin-top: 12px;
  padding: 10px 14px;
  background: #f0faf4;
  border-radius: 8px;
  font-size: 0.8rem;
  color: #444;
  line-height: 1.8;
}
.bq-status-legend--result {
  margin-top: 20px;
  margin-bottom: 4px;
}
.bq-status-legend__title { font-weight: 600; margin-bottom: 2px; color: #333; }
.bq-status-legend p { margin: 0; }

.bq-result__pending-hint {
  font-size: 0.85rem;
  color: #856404;
  background: #fff8e1;
  border-radius: 6px;
  padding: 8px 12px;
  margin: 0 0 8px;
  font-weight: 500;
}
.bq-result__confirmed-hint {
  font-size: 0.85rem;
  color: #0c5460;
  background: #d1f0ea;
  border-radius: 6px;
  padding: 8px 12px;
  margin: 0 0 8px;
  font-weight: 500;
}
.bq-result__line-action {
  margin: 8px 0 4px;
}
.bq-line-user { display: flex; align-items: center; gap: 10px; background: #f0faf4; border: 1px solid #c3e6cb; border-radius: 8px; padding: 12px 16px; margin-bottom: 16px; }
.bq-line-user__avatar { width: 36px; height: 36px; border-radius: 50%; }
.bq-line-user__name { font-weight: 600; color: #333; flex: 1; }
.bq-line-user__logout { background: none; border: 1px solid #aaa; color: #888; border-radius: 6px; padding: 4px 10px; font-size: 0.8rem; cursor: pointer; }
.bq-line-loading { display: flex; align-items: center; gap: 8px; font-size: 0.9rem; color: #888; margin-top: 12px; }

/* 變更時間 Modal */
.bq-rs-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}
.bq-rs-modal {
  background: #fff;
  border-radius: 14px;
  padding: 24px 20px 20px;
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
}
.bq-rs-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.bq-rs-modal__title { font-weight: 700; font-size: 1.05rem; color: #333; }
.bq-rs-close {
  background: none;
  border: none;
  font-size: 1.1rem;
  color: #aaa;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 4px;
  line-height: 1.4;
}
.bq-rs-close:hover { background: #f0f0f0; color: #666; }
/* 進場動畫 */
.bq-rs-fade-enter-active, .bq-rs-fade-leave-active { transition: opacity 0.2s; }
.bq-rs-fade-enter-from, .bq-rs-fade-leave-to { opacity: 0; }
.bq-rs-fade-enter-active .bq-rs-modal,
.bq-rs-fade-leave-active .bq-rs-modal { transition: transform 0.2s; }
.bq-rs-fade-enter-from .bq-rs-modal { transform: translateY(16px); }
.bq-rs-fade-leave-to .bq-rs-modal { transform: translateY(8px); }
.bq-rs-note {
  font-size: 0.82rem;
  color: #856404;
  background: #fff3cd;
  border-radius: 6px;
  padding: 8px 12px;
  margin: 0 0 14px;
  line-height: 1.5;
}
.bq-rs-date-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.bq-rs-label { font-size: 0.88rem; color: #666; white-space: nowrap; }
.bq-rs-date-input { max-width: 180px; padding: 8px 12px; margin: 0; }
.bq-rs-loading { display: flex; align-items: center; gap: 8px; font-size: 0.88rem; color: #888; margin-bottom: 12px; }
.bq-rs-error {
  font-size: 0.85rem;
  color: #c0392b;
  background: #fdf0ef;
  border-radius: 6px;
  padding: 8px 12px;
  margin: 0 0 12px;
}
.bq-rs-error--submit { margin-top: 12px; margin-bottom: 0; }
.bq-rs-slots {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
  max-height: 280px;
  overflow-y: auto;
}
.bq-rs-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 11px 16px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: #fff;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  color: #444;
  width: 100%;
  text-align: center;
}
.bq-rs-slot:hover { border-color: #8b6f5c; color: #8b6f5c; background: #fdf8f5; }
.bq-rs-slot--selected { background: #8b6f5c; border-color: #8b6f5c; color: #fff; }
.bq-rs-slot--selected .bq-rs-slot__end { color: rgba(255,255,255,0.85); }
.bq-rs-slot__end { color: #999; font-size: 0.9em; }
.bq-rs-slot--full { background: #f5f5f5; border-color: #e0e0e0; color: #bbb; cursor: not-allowed; }
.bq-rs-slot--full .bq-rs-slot__end { color: #ccc; }
.bq-rs-slot__full-tag {
  float: right;
  font-size: 0.78em;
  color: #e74c3c;
  font-weight: 500;
}
.bq-rs-footer {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.bq-rs-footer .bq-btn { flex: 1; min-width: 110px; }
.bq-rs-success-body { padding: 4px 0 16px; }
.bq-rs-success-msg {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #444;
  margin: 0 0 16px;
}
.bq-rs-success-actions { margin-top: 0; }
</style>
